import type {
  ConsumiAssistente, DividiInput, ElencoAzioni, EsitoAnnulla, ModelloChat, RispostaDividi, RispostaRiscrivi,
} from '@masamune/shared';
import { api, ApiError, leggiFlusso } from '../../lib/api';

/**
 * Lo stato dell'assistente (GET /ai/stato):
 * - attiva: risponde con Claude;
 * - finta: risposte di prova, solo in locale;
 * - spenta: l'interruttore «Acceso» è spento;
 * - sospesa: si è fermata da sola (chiave rifiutata, crediti finiti);
 * - senza-chiave: sul server manca la chiave.
 */
export interface StatoAssistente {
  stato: 'attiva' | 'finta' | 'spenta' | 'sospesa' | 'senza-chiave';
  modello: string;
  /**
   * Il modello della chat di chi guarda (30/10/2026): quello scelto col tasto nel pannello, o
   * `modello`. Facoltativo: un'API di prima non lo manda.
   */
  modelloChat?: string;
  budgetUsd: number;
  spesiUsd: number;
  restanoUsd: number;
  budgetGiornoUsd: number;
  spesiOggiUsd: number;
  /**
   * Fermo per il tetto di oggi o per il budget del mese (29/10/2026): lo calcola il server con
   * la regola che ferma la chat, cioè la spesa più la prenotazione minima di una domanda. La
   * spesa da sola non arriva quasi mai al tetto pieno, e l'intestazione restava «Pronto».
   */
  fermoOggi: boolean;
  fermoMese: boolean;
  /**
   * I vocali (2G, 29/10/2026): c'è la chiave di Groq sul server, o è la modalità finta. Senza, il
   * microfono non c'è. Facoltativo: un'API di prima non lo manda.
   */
  vocali?: boolean;
  impostazioni: {
    acceso: boolean;
    soloLettura: boolean;
    memoriaDiNotte: boolean;
    sospesaIl: string | null;
    motivoSospensione: string | null;
  };
}

/** Il modello della chat di chi chiede (PUT /ai/modello, 30/10/2026): torna lo stato col modello nuovo. */
export const scegliModello = (modello: ModelloChat) => api.put<StatoAssistente>('/ai/modello', { modello });

export const statoAssistenteQuery = {
  queryKey: ['ai', 'stato'] as const,
  queryFn: () => api.get<StatoAssistente>('/ai/stato'),
  refetchOnWindowFocus: false,
  retry: false,
  staleTime: 60_000,
};

/**
 * Si può chiedere qualcosa: attiva o, in locale, finta. Dal 29/10/2026 (revisione della 2B) non
 * quando è fermo per il tetto di oggi o per il budget del mese: così spariscono anche «Chiedi a
 * MUNE» nella ricerca e i tasti di Dividi e Riscrivi, che prenoterebbero di più.
 */
export const puoRispondere = (s?: StatoAssistente) =>
  s?.stato === 'finta' || (s?.stato === 'attiva' && !s.fermoOggi && !s.fermoMese);

const FMT_MESE = new Intl.DateTimeFormat('it-IT', { month: 'long', timeZone: 'UTC' });

/**
 * «MUNE è fermo fino al 1° dicembre: il budget di novembre è finito.», coi mesi veri. Il
 * server conta il mese in UTC (ai.core.ts, inizioMese): qui lo stesso.
 */
function fraseBudgetFinito(ora = new Date()): string {
  const anno = ora.getUTCFullYear();
  const mese = ora.getUTCMonth();
  const questo = FMT_MESE.format(new Date(Date.UTC(anno, mese, 15)));
  const prossimo = FMT_MESE.format(new Date(Date.UTC(anno, mese + 1, 15)));
  return `MUNE è fermo fino al 1° ${prossimo}: il budget di ${questo} è finito.`;
}

/**
 * Perché non risponde, detto a chi guarda: nell'intestazione del pannello al posto di
 * «Pronto», e al posto del saluto a chat vuota. Dal 29/10/2026 (fetta 2B) anche quando è
 * acceso ma ha speso il tetto di oggi o il budget del mese: la domanda prenderebbe 402. Lo
 * dice il server (fermoOggi, fermoMese), che conta anche la prenotazione di una domanda: il
 * confronto della spesa col tetto pieno non scattava quasi mai (revisione della 2B). Le
 * frasi sono quelle degli errori AI_BUDGET_GIORNO e AI_BUDGET della chat
 * (os-planning/ai/fase-2-progetto.md, «Contratti»). In prova (finta) i tetti non contano:
 * le risposte finte non costano e il server non li guarda.
 */
export function fraseStato(s?: StatoAssistente): string | null {
  if (!s) return null;
  switch (s.stato) {
    case 'spenta':
      return 'MUNE è spento: si accende in Impostazioni › Servizi collegati.';
    case 'sospesa':
      return `MUNE si è fermato${s.impostazioni.motivoSospensione ? `: ${s.impostazioni.motivoSospensione}` : ''}. Si riaccende in Impostazioni › Servizi collegati.`;
    case 'senza-chiave':
      return 'MUNE non ha ancora la chiave di Claude sul server.';
    case 'attiva':
      if (s.fermoOggi) return 'MUNE è fermo fino a domani: ha speso il tetto di oggi.';
      if (s.fermoMese) return fraseBudgetFinito();
      return null;
    default:
      return null;
  }
}

/**
 * I codici d'errore che vogliono dire «adesso MUNE non può rispondere» (29/10/2026): dopo uno
 * di questi, prima del flusso o dentro, il pannello rilegge lo stato (['ai', 'stato']) e
 * l'intestazione smette di dire «Pronto».
 */
export const CODICI_MUNE_FERMO: ReadonlySet<string> = new Set([
  'AI_SPENTA', 'AI_SOSPESA', 'AI_SENZA_CHIAVE', 'AI_BUDGET_GIORNO', 'AI_BUDGET',
]);

export function salvaImpostazioni(body: { acceso?: boolean; soloLettura?: boolean; memoriaDiNotte?: boolean }) {
  return api.patch<StatoAssistente>('/ai/impostazioni', body);
}

/**
 * «Prova lo streaming» in Impostazioni (29/10/2026, fetta 2A della Fase 2): legge
 * GET /ai/prova-flusso, che manda cinque `pezzo` uno al secondo e poi `fine`, senza
 * chiamare Claude (costo zero). Serve a vedere, dietro l'nginx vero, che i pezzi arrivano
 * man mano: la chat in streaming della 2B ne ha bisogno. `aPezzi` se tra il primo e
 * l'ultimo pezzo passano almeno 2,5 secondi; se arrivano tutti insieme alla fine, qualcosa
 * sulla strada tiene la risposta (il buffer di nginx) e la chat comparirebbe di colpo.
 * `secondi` è proprio quel tempo, dal primo all'ultimo pezzo. Senza `fine` il flusso si è
 * interrotto a metà: errore, con la frase di «Parole a schermo».
 * Parte solo dal tasto, come le altre chiamate di MUNE.
 */
export async function provaFlusso(): Promise<{ pezzi: number; secondi: number; aPezzi: boolean }> {
  const visto = { pezzi: 0, primo: null as number | null, ultimo: 0, fine: false };
  await leggiFlusso('/ai/prova-flusso', {
    onEvento: (nome) => {
      if (nome === 'pezzo') {
        const ora = performance.now();
        visto.pezzi += 1;
        visto.primo ??= ora;
        visto.ultimo = ora;
      } else if (nome === 'fine') {
        visto.fine = true;
      }
    },
  });
  if (!visto.fine) throw new ApiError(502, 'FLUSSO_INTERROTTO', 'La connessione si è interrotta: riprova.');
  const ms = visto.primo === null ? 0 : visto.ultimo - visto.primo;
  return { pezzi: visto.pezzi, secondi: Math.round(ms / 100) / 10, aPezzi: ms >= 2500 };
}

// ── Dividi, Riscrivi, il registro (os-planning/ai/fase-1-progetto.md) ──
// Le chiamate a pagamento (dividi, riscrivi) solo da un clic, con useMutation: mai da
// useEffect o useQuery (in locale React.StrictMode le farebbe partire due volte).

/** «Dividi con MUNE» e «Dividi in più task»: la proposta, da confermare con «Crea N task». */
export function dividi(body: DividiInput) {
  return api.post<RispostaDividi>('/ai/dividi', body);
}

/** «Riscrivi titolo e descrizione»: la proposta, da confermare con «Usa questo testo». */
export function riscrivi(taskId: string) {
  return api.post<RispostaRiscrivi>('/ai/riscrivi', { taskId });
}

/** L'Annulla di un'azione dell'Assistente (anche ad Assistente spento: non chiama il modello). */
export function annullaAzione(id: string) {
  return api.post<EsitoAnnulla>(`/ai/azioni/${encodeURIComponent(id)}/annulla`);
}

/** «Cosa ha fatto MUNE»: le azioni più nuove prima, a pagine (`prima` = l'id dell'ultima vista). */
export const azioniQuery = (prima?: string | null) => ({
  queryKey: ['ai', 'azioni', prima ?? null] as const,
  queryFn: () => api.get<ElencoAzioni>(`/ai/azioni${prima ? `?prima=${encodeURIComponent(prima)}` : ''}`),
  staleTime: 15_000,
});

/** La spesa del mese ('YYYY-MM'), per funzione e per persona. */
export const consumiQuery = (mese?: string) => ({
  queryKey: ['ai', 'consumi', mese ?? null] as const,
  queryFn: () => api.get<ConsumiAssistente>(`/ai/consumi${mese ? `?mese=${encodeURIComponent(mese)}` : ''}`),
  staleTime: 60_000,
});

// ── Le schede memoria ──

export type SezioneMemoria = 'CHI_SONO' | 'NOMI' | 'PERSONE' | 'ACCORDI' | 'REGOLE' | 'TONO' | 'IN_SOSPESO' | 'DECISIONI' | 'CIFRE';

export const SEZIONI_MEMORIA: Array<{ codice: SezioneMemoria; nome: string }> = [
  { codice: 'CHI_SONO', nome: 'Chi sono' },
  { codice: 'NOMI', nome: 'Come lo chiamiamo' },
  { codice: 'PERSONE', nome: 'Persone' },
  { codice: 'ACCORDI', nome: 'Accordi e servizi' },
  { codice: 'REGOLE', nome: 'Regole da rispettare' },
  { codice: 'TONO', nome: 'Come scrivergli' },
  { codice: 'IN_SOSPESO', nome: 'Cose in sospeso' },
  { codice: 'DECISIONI', nome: 'Decisioni prese' },
  { codice: 'CIFRE', nome: 'Cifre' },
];

export interface RigaMemoria {
  id: string;
  sezione: SezioneMemoria;
  testo: string;
  fonte: string;
  dedotta: boolean;
  bloccata: boolean;
  autore: 'IMPORT' | 'NOTTE' | 'ASSISTENTE' | 'PERSONA';
  aggiornata: string;
}

export interface SchedaMemoria {
  tipo: 'cliente' | 'progetto';
  id: string;
  nome: string;
  righe: RigaMemoria[];
  aggiornata: string | null;
}

export const schedaQuery = (tipo: 'cliente' | 'progetto', id: string) => ({
  queryKey: ['ai', 'memoria', tipo, id] as const,
  queryFn: () => api.get<SchedaMemoria>(`/ai/memoria?${tipo === 'cliente' ? 'clientId' : 'projectId'}=${encodeURIComponent(id)}`),
});

export function dollari(n: number): string {
  return `${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

/** claude-haiku-5-5 → Haiku 5.5 */
export function nomeModello(id: string): string {
  const m = /claude-(haiku|sonnet|opus)-(\d+)-(\d+)/.exec(id);
  if (!m) return id;
  return `${m[1]!.charAt(0).toUpperCase()}${m[1]!.slice(1)} ${m[2]}.${m[3]}`;
}
